import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { HeaderWithBreadcrumbs } from "components/molecules/HeaderWithBreadcrumbs/HeaderWithBreadbrumbs";
import { useCourse } from "api/hooks";
import { useCourseAttendance, useCourseAttendanceDays } from "api/hooks/useCourseAttendance";
import { PageMain } from "components/molecules";
import { PaziaBadge, PaziaLoader } from "components/atoms";
import { breadcrumbs } from "pages/breadcrumbs";
import { formatDate } from "utils";
import { AttendanceStatusApi, toLocalIsoDate } from "api/courseAttendanceApi";

const parseLocalIsoDate = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

export const CourseAttendancePage = () => {
  const { courseId } = useParams();
  const { loading: loadingCourse, data: course } = useCourse(Number(courseId));
  const { isLoading: loadingDays, days } = useCourseAttendanceDays(Number(courseId));
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  useEffect(() => {
    if (!selectedDay && days.length > 0) setSelectedDay(days[days.length - 1]);
  }, [days, selectedDay]);

  const { isLoading: loadingAttendance, attendance } = useCourseAttendance(Number(courseId), selectedDay);

  if (loadingCourse || loadingDays) return <PaziaLoader position="center" />;
  if (!course) return null;

  return (
    <PageMain>
      <HeaderWithBreadcrumbs
        breadcrumbs={breadcrumbs.COURSE_ATTENDANCE(
          course.group.name,
          course.group.id,
          course.data.edition,
          course.data.id,
          course.data.variant,
        )}
        headerText="Obecność"
      />

      {days.length === 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400">
          Kurs nie ma jeszcze żadnych dni w harmonogramie.
        </div>
      )}

      {days.length > 0 && (
        <>
          <div className="mb-4 flex items-center gap-2">
            <label htmlFor="attendance-day" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Dzień:
            </label>
            <select
              id="attendance-day"
              // form-select keeps room for the arrow (px-3 used to let the date run under it: "202ϵ")
              className="form-select py-1.5 text-sm"
              value={selectedDay ? toLocalIsoDate(selectedDay) : ""}
              onChange={(e) => setSelectedDay(parseLocalIsoDate(e.target.value))}
            >
              {days.map((day) => (
                <option key={toLocalIsoDate(day)} value={toLocalIsoDate(day)}>
                  {formatDate(day)}
                </option>
              ))}
            </select>
          </div>

          {loadingAttendance || !attendance ? (
            <PaziaLoader position="center" />
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="border-b border-gray-200 text-xs uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                <tr>
                  <th className="py-2">Kursant</th>
                  <th className="py-2">Obecność</th>
                </tr>
              </thead>
              <tbody>
                {attendance.students.map((student) => (
                  <tr key={student.studentUserId} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-2">
                      {student.firstName} {student.surname}
                    </td>
                    <td className="py-2">
                      <AttendanceBadge status={student.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </PageMain>
  );
};

const AttendanceBadge = ({ status }: { status: AttendanceStatusApi | null }) => {
  if (status === AttendanceStatusApi.Present) return <PaziaBadge color="emerald">Obecny</PaziaBadge>;
  if (status === AttendanceStatusApi.Absent) return <PaziaBadge color="amber">Nieobecny</PaziaBadge>;
  return <PaziaBadge color="slate">Brak wpisu</PaziaBadge>;
};
